<script setup lang="ts">
  import { computed, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { type Plan, SchemeType } from '@/shared/types/api.types'
  import { useGoogleAnalytics, useLinkTarget } from '@/shared/composables'
  import { useUserStore } from '@/shared/store'
  import { getVariant } from '@/widgets/Flow/FlowSelectPlanWidget/FlowSelectPlanWidget.utils'
  import {
    LOAD_EVENT_NAME,
    PLAN_DESCRIPTION_SCHEME_MAPPING,
    PROCEED_EVENT_NAME,
    SINGLE_VARIANT,
  } from './FlowSelectPlanWidget.constants'
  import { type Locale, TravelDirection } from '@/shared/types/common.types'
  import InformationCardInsuranceValues from '@/shared/components/InformationCard/InformationCardInsuranceValues.vue'
  import FlowPlanInsuranceValues from '@/entities/Flow/components/FlowPlan/FlowPlanInsuranceValues.vue'
  import TooltipInsuranceValues from '@/shared/components/TooltipInsuranceValues/TooltipInsuranceValues.vue'
  import type { Props } from '@/widgets/Flow/FlowSelectPlanWidget/FlowSelectPlanWidget.types'
  import FlowPlanLearnMoreModal from '@/entities/Flow/components/FlowPlan/components/FlowPlanLearnMoreModal/FlowPlanLearnMoreModal.vue'
  import { schemeBadgeMap } from '@/entities/Flow/components/FlowPlan/FlowPlanInsuranceValues.constants'
  import { useRoute } from 'vue-router'
  import type { AvailableFlowRoutes } from '@/shared/types/flow.types'
  import { toFixedNumber, formatDateDefault } from '@/shared/utils'
  import FlowCardQC from '@/shared/components/FlowCardQC/FlowCardQC.vue'
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'

  interface Emits {
    (e: typeof PROCEED_EVENT_NAME): void

    (e: typeof LOAD_EVENT_NAME, request: () => Promise<any>): void
    (e: 'back'): void
  }

  const props = defineProps<Props>()
  const emit = defineEmits<Emits>()

  const { t, locale } = useI18n()
  const gaService = useGoogleAnalytics()
  const route = useRoute()
  const { getLinkTarget } = useLinkTarget()

  const userStore = useUserStore()

  const adultsCount = computed(() => userStore.adultsCount)
  const childrenCount = computed(() => userStore.childrenCount)
  const travelersCount = adultsCount.value + childrenCount.value

  const departureDate = ref<Date | null>(userStore.departureDate)
  const returnDate = ref<Date | null>(userStore.returnDate)

  const isSingleTrip = computed(() => userStore.tripType === 'SINGLE')
  const isModalOpen = ref(false)
  const activePopupPlanSchemeTypeCode = ref<SchemeType>()
  const plansSchemeTypeCodes = computed(() => props.plans.map((plan) => plan.schemeTypeCode))

  const onProceed = () => {
    emit(PROCEED_EVENT_NAME)
  }

  const handleSelectPlan = async (plan: Plan) => {
    const countries = userStore.destination.map((el) => el.id)

    userStore.setSelectedPlan(plan.schemecode, true)
    gaService.sendOutboundSelectPlanProceed(
      isSingleTrip.value,
      plan,
      countries,
      String(adultsCount.value + childrenCount.value),
      plan.coinsDiscountValue ? toFixedNumber(plan.coinsDiscountValue) : undefined
    )
    onProceed()
  }

  const destinationText = computed(() => {
    const ids = userStore.destination.map((el) => el.id)
    return props.listOfDestionations
      .filter((el) => ids.includes(el.id))
      .map((el) => el.name)
      .join(', ')
  })

  const informationCardOptions = computed(() => [
    {
      id: 'date',
      content: `${formatDateDefault(departureDate.value!, locale.value as Locale)} - ${formatDateDefault(returnDate.value!, locale.value as Locale)}`,
    },
    {
      id: 'travelers',
      content: `${t(
        'outbound.select_plan.insurance_values.travelers',
        { travelersCount },
        travelersCount
      )}`,
    },
  ])

  const isSinglePlan = computed(() => props.plans.length === 1)
  const tripTypeText = computed(() =>
    isSingleTrip.value ? t('outbound_travel_type_single') : t('outbound_travel_type_multi')
  )

  const onPolicyWordingClick = () => {
    window.open(props.policyWording, getLinkTarget())
  }

  const sendCoverToggle = (planName: string, planPrice: number, coverName: string) => {
    gaService.sendCoverToggle({
      category: TravelDirection.OUTBOUND,
      numOfTravelers: travelersCount,
      section: 'inclusive_cover',
      planName,
      planPrice,
      coverName,
    })
  }

  const selectPlanButtonTxt = computed(() =>
    isSinglePlan.value ? t('quote_proceed') : t('travel.outbound.select_plan.select_button')
  )
  const recommendedPlanDescription = (schemeType: SchemeType) => {
    const schemeTypes = props.plans.map((plan) => plan.schemeTypeCode).sort()
    const uniqueSchemeTypes = [...new Set(schemeTypes)].join(',')
    return PLAN_DESCRIPTION_SCHEME_MAPPING[schemeType][uniqueSchemeTypes].map((item) => t(item))
  }

  const planDescriptionTooltipInfo = (schemeType: SchemeType) => {
    const types = props.plans
      .map((p) => p.schemeTypeCode)
      .sort()
      .join(',')

    if (
      schemeType === SchemeType.WorldwideExcl &&
      types !== `${SchemeType.Worldwide},${SchemeType.WorldwideExcl}`
    ) {
      return t('outbound.select_plan.insurance_values.plan_tooltip.worldwideexcl')
    }

    if (
      schemeType === SchemeType.Regional &&
      types === `${SchemeType.Regional},${SchemeType.WorldwideExcl}`
    ) {
      return [
        t('outbound.select_plan.insurance_values.plan_tooltip.regional_1'),
        t('outbound.select_plan.insurance_values.plan_tooltip.regional_2'),
        t('outbound.select_plan.insurance_values.plan_tooltip.regional_3'),
        t('outbound.select_plan.insurance_values.plan_tooltip.regional_4'),
      ]
    }

    if (schemeType === SchemeType.Worldwide && types === `${SchemeType.Worldwide}`) {
      return t('outbound.select_plan.insurance_values.plan_tooltip.worldwide')
    }
    return ''
  }

  const isPlanRecommended = (index: number) => (isSinglePlan.value ? false : index === 0)

  const tooltipColor = (index: number) => (isPlanRecommended(index) ? '#FFFFFF' : '#394B5ACC')

  const openLearnMorePopup = (schemeTypeCode: SchemeType) => {
    activePopupPlanSchemeTypeCode.value = schemeTypeCode
    isModalOpen.value = true
    gaService.sendClickLearnMore(route.name as AvailableFlowRoutes, { section: 'plans_block' })
  }

  const badgeText = (plan: Plan) => {
    const plansSchemeTypeCodes = props.plans.map((p) => p.schemeTypeCode).sort()
    const uniqueSchemeTypes = [...new Set(plansSchemeTypeCodes)].join(',')

    if (uniqueSchemeTypes === `${SchemeType.FlyEurope},${SchemeType.WorldwideExcl}`) {
      const key = schemeBadgeMap[plan.schemeTypeCode]
      return key ? t(key) : ''
    }
    return ''
  }
</script>

<template>
  <FlowCardQC
    :title="
      isSinglePlan
        ? t('outbound.select_plan.insurance_values.header_one_plan')
        : t('outbound.select_plan.insurance_values.header_two_plans')
    "
    :with-button="false"
    :class="{ 'flow-card--wide': plans.length > 1 }"
    :with-back-button="withBackButton"
    @back="emit('back')"
  >
    <InformationCardInsuranceValues
      :description="destinationText"
      :options="informationCardOptions"
      variant="travel"
      class="flow-select-plan-widget__information"
    >
      <template #trip-type>
        {{ tripTypeText }}
      </template>
    </InformationCardInsuranceValues>
    <div class="flow-select-plan-widget__plans">
      <FlowPlanInsuranceValues
        v-for="(plan, planIndex) in plans"
        :key="plan.schemecode"
        :is-recomended="isPlanRecommended(planIndex)"
        :variant="isSinglePlan ? SINGLE_VARIANT : getVariant(plan.schemecode)"
        :policy-wording="policyWording"
        :plan="plan"
        :is-single-plan="isSinglePlan"
        :select-plan-button-txt="selectPlanButtonTxt"
        :type-of-travel="TravelDirection.OUTBOUND"
        :badge-text="badgeText(plan)"
        :travelers-count="travelersCount"
        @select-plan="() => handleSelectPlan(plan)"
        @policy-wording-click="() => onPolicyWordingClick"
        @toggle="(_, __, coverName) => sendCoverToggle(plan.name, plan.price, coverName)"
        @open-learn-more-popup="openLearnMorePopup"
      >
        <template #plan-description>
          <span>{{ recommendedPlanDescription(plan.schemeTypeCode).join(' ') }}</span>
          <TooltipInsuranceValues
            :info="planDescriptionTooltipInfo(plan.schemeTypeCode)"
            :size="16"
            :color="tooltipColor(planIndex)"
            class="description-tooltip"
          />
        </template>
      </FlowPlanInsuranceValues>
    </div>
    <ButtonQC
      v-if="isSinglePlan"
      wide
      data-test="select-plan-single-button"
      @click="handleSelectPlan(plans[0])"
    >
      {{ selectPlanButtonTxt }}
    </ButtonQC>
  </FlowCardQC>

  <FlowPlanLearnMoreModal
    v-if="isModalOpen"
    v-model="activePopupPlanSchemeTypeCode"
    :plans-scheme-type-codes="plansSchemeTypeCodes"
    data-test="select-plan-learn-more-modal"
    @close="isModalOpen = false"
    @policy-wording-click="onPolicyWordingClick"
  />
</template>

<style scoped lang="scss">
  @use './FlowSelectPlanWidget';
  @use '@/shared/assets/styles/utils' as *;

  $flow-width: 768px;

  .flow-select-plan-widget {
    &__information {
      margin-bottom: var(--spacing-7);

      @include mobile-flow($flow-width) {
        grid-template-columns: 1fr;
      }
    }

    &__plans {
      grid-template-columns: repeat(auto-fit, minmax(47%, 1fr));
      gap: var(--spacing-5);

      @include mobile-flow($flow-width) {
        grid-template-columns: 1fr;
        gap: var(--spacing-8);
      }
    }
  }
</style>
